import { useState, useEffect } from 'react';
import { C, FONTS } from '../../tokens/design';
import { useApiClient } from '../../api/apiClient';
import PlatformShell from '../../components/layout/PlatformShell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';

const CHECKS = [
  { id: 'portalName', label: 'Portal Name configured', category: 'Branding', test: b => !!b?.portalName },
  { id: 'logoUrl', label: 'Logo uploaded', category: 'Branding', test: b => !!b?.logoUrl },
  { id: 'primaryColor', label: 'Primary color set (not default)', category: 'Branding', test: b => b?.primaryColor && b.primaryColor !== '#ef4444' },
  { id: 'secondaryColor', label: 'Secondary color set (not default)', category: 'Branding', test: b => b?.secondaryColor && b.secondaryColor !== '#7c3aed' },
  { id: 'tagline', label: 'Tagline configured', category: 'Branding', test: b => !!b?.tagline },
  { id: 'oidcAuthority', label: 'SSO / OIDC Authority configured', category: 'Authentication', test: b => !!b?.oidcAuthority },
  { id: 'oidcClientId', label: 'OIDC Client ID configured', category: 'Authentication', test: b => !!b?.oidcClientId },
  { id: 'hasClients', label: 'At least one client onboarded', category: 'Operations', test: (_, c) => c && c.length > 0 },
  { id: 'hasActiveClient', label: 'At least one client with completed scan', category: 'Operations', test: (_, c) => c && c.some(cl => cl.lastScanAt) },
];

function CheckRow({ check, passed }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
      borderBottom: `1px solid ${C.b2}`,
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: passed ? `${C.grn}15` : `${C.red}10`,
        border: `1px solid ${passed ? C.grn : C.red}33`,
        color: passed ? C.grn : C.red, fontSize: 11, fontWeight: 700,
      }}>
        {passed ? '\u2713' : '\u2717'}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: C.t1, fontFamily: FONTS.sans }}>{check.label}</div>
      </div>
      <span style={{
        fontSize: 9, fontFamily: FONTS.mono, padding: '2px 6px', borderRadius: 4,
        background: passed ? `${C.grn}10` : `${C.amb}10`,
        color: passed ? C.grn : C.amb,
        border: `1px solid ${passed ? C.grn : C.amb}33`,
      }}>
        {passed ? 'PASS' : 'INCOMPLETE'}
      </span>
    </div>
  );
}

export default function WhiteLabelVerify() {
  const api = useApiClient();
  const [loading, setLoading] = useState(true);
  const [msps, setMsps] = useState([]);
  const [selectedMsp, setSelectedMsp] = useState(null);
  const [branding, setBranding] = useState(null);
  const [clients, setClients] = useState([]);
  const [checkLoading, setCheckLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/platform/msps');
        setMsps(Array.isArray(data) ? data : data?.msps || []);
      } catch {
        setMsps([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const runChecks = async (mspId) => {
    setSelectedMsp(mspId);
    setCheckLoading(true);
    setBranding(null);
    setClients([]);
    try {
      // /platform/msps/{id} returns { profile, branding, teamMembers, clients }
      const data = await api.get(`/platform/msps/${mspId}`);
      setBranding(data?.branding || null);
      setClients(data?.clients || []);
    } catch {
      // use defaults
    } finally {
      setCheckLoading(false);
    }
  };

  const results = branding !== null ? CHECKS.map(ch => ({
    ...ch,
    passed: ch.test(branding, clients),
  })) : [];

  const passCount = results.filter(r => r.passed).length;
  const totalCount = results.length;
  const categories = [...new Set(CHECKS.map(c => c.category))];

  if (loading) {
    return (
      <PlatformShell title="White-Label Verification" sub="Verify MSP portal readiness">
        <Card style={{ padding: 22 }}><Skeleton height={200} /></Card>
      </PlatformShell>
    );
  }

  return (
    <PlatformShell title="White-Label Verification" sub="Verify MSP portal readiness">
      <Card style={{ padding: 20, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12 }}>Select MSP to Verify</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {msps.length === 0 && (
            <div style={{ fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>No MSPs found.</div>
          )}
          {msps.map(m => {
            const id = m.mspId || m.id;
            const name = m.name || m.mspName || id;
            const active = selectedMsp === id;
            return (
              <button key={id} onClick={() => runChecks(id)} style={{
                padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
                background: active ? `${C.sky}15` : C.s2,
                border: `1px solid ${active ? C.sky : C.b1}`,
                color: active ? C.sky : C.t1, fontSize: 11, fontWeight: 600, fontFamily: FONTS.sans,
              }}>
                {name}
              </button>
            );
          })}
        </div>
      </Card>

      {checkLoading && (
        <Card style={{ padding: 22 }}>
          <Skeleton height={180} />
        </Card>
      )}

      {!checkLoading && selectedMsp && branding !== null && (
        <>
          {/* Score summary */}
          <Card style={{ padding: '14px 20px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  fontSize: 28, fontWeight: 800, fontFamily: FONTS.mono,
                  color: passCount === totalCount ? C.grn : passCount >= totalCount * 0.7 ? C.amb : C.red,
                }}>
                  {passCount}/{totalCount}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans }}>Checks Passing</div>
                  <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
                    {passCount === totalCount ? 'Portal is ready for production' : `${totalCount - passCount} item${totalCount - passCount > 1 ? 's' : ''} remaining`}
                  </div>
                </div>
              </div>
              <div style={{ width: 120 }}>
                <div style={{ height: 6, background: C.b1, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(passCount / totalCount) * 100}%`, background: passCount === totalCount ? C.grn : C.amb, borderRadius: 3 }} />
                </div>
              </div>
            </div>
          </Card>

          {/* Checks by category */}
          {categories.map(cat => (
            <Card key={cat} style={{ marginBottom: 14 }}>
              <CardHeader title={cat} />
              {results.filter(r => r.category === cat).map(r => (
                <CheckRow key={r.id} check={r} passed={r.passed} />
              ))}
            </Card>
          ))}

          {/* Current branding values */}
          <Card style={{ padding: 20, marginBottom: 14 }}>
            <CardHeader title="Current Branding Values" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '10px 14px' }}>
              {[
                { l: 'Portal Name', v: branding?.portalName },
                { l: 'Primary Color', v: branding?.primaryColor },
                { l: 'Secondary Color', v: branding?.secondaryColor },
                { l: 'Background Color', v: branding?.backgroundColor },
                { l: 'Text Color', v: branding?.textColor },
                { l: 'Logo URL', v: branding?.logoUrl ? 'Uploaded' : 'Not set' },
                { l: 'OIDC Authority', v: branding?.oidcAuthority || 'Not configured' },
                { l: 'OIDC Client ID', v: branding?.oidcClientId || 'Not configured' },
              ].map(r => (
                <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.b2}` }}>
                  <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{r.l}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, fontFamily: FONTS.mono, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {r.v || '\u2014'}
                    {r.l.includes('Color') && r.v && r.v.startsWith('#') && (
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: r.v, border: `1px solid ${C.b1}` }} />
                    )}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Manual verification checklist */}
          <Card style={{ padding: 20, marginBottom: 14 }}>
            <CardHeader title="Manual Verification Checklist" />
            <div style={{ padding: '10px 14px', fontSize: 11, fontFamily: FONTS.mono, color: C.t2, lineHeight: 2.2 }}>
              {[
                'Logo displays correctly',
                `Primary color (${branding?.primaryColor || 'N/A'}) applied to buttons and accents`,
                `Secondary color (${branding?.secondaryColor || 'N/A'}) applied to text`,
                `Background color (${branding?.backgroundColor || 'N/A'}) applied`,
                `Portal name shows "${branding?.portalName || 'N/A'}" not "SentinelCommand"`,
                'Login redirects to correct tenant',
                'After login lands on MSP dashboard not platform dashboard',
              ].map((item, i) => (
                <div key={i}>{'\u2610'} {item}</div>
              ))}
            </div>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, gap: 8 }}>
            <Button variant="sec" onClick={() => runChecks(selectedMsp)}>Refresh Branding</Button>
            <Button variant="sec" onClick={() => runChecks(selectedMsp)}>Re-run Checks</Button>
          </div>
        </>
      )}

      {/* CyberSurv quick-access section */}
      <Card style={{ padding: 20, marginTop: 14 }}>
        <CardHeader title="CyberSurv Partner Portal" />
        <div style={{ padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 12 }}>
            Quick access to the CyberSurv white-label portal for manual verification.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
            <a
              href="https://cybersurv.southernsentinelsecurity.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block', padding: '8px 16px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                fontFamily: FONTS.sans, background: '#C8102E', color: '#fff', textDecoration: 'none',
              }}
            >
              Open CyberSurv Portal
            </a>
            <Button variant="sec" onClick={() => {
              const cybersuvMspId = '00000000-0000-0000-0000-000000000002';
              runChecks(cybersuvMspId);
            }}>
              Refresh CyberSurv Branding
            </Button>
          </div>
          <div style={{ fontSize: 10, fontFamily: FONTS.mono, color: C.t2, lineHeight: 2 }}>
            <div>Expected branding:</div>
            <div>{'\u2022'} Primary: <span style={{ color: '#C8102E', fontWeight: 700 }}>#C8102E (red)</span></div>
            <div>{'\u2022'} Secondary: <span style={{ color: '#002868', fontWeight: 700 }}>#002868 (navy)</span></div>
            <div>{'\u2022'} Background: <span style={{ fontWeight: 700 }}>#ffffff (white)</span></div>
            <div>{'\u2022'} Portal name: CyberSurv</div>
          </div>
        </div>
      </Card>

      {!checkLoading && selectedMsp && branding === null && (
        <Card style={{ padding: 22 }}>
          <div style={{ textAlign: 'center', color: C.t3, fontFamily: FONTS.mono, fontSize: 12 }}>
            Could not load branding data for this MSP. The MSP may not have configured branding yet.
          </div>
        </Card>
      )}
    </PlatformShell>
  );
}
